/**
 * share.js — mobile-first Story image sharing helpers.
 *
 * The platform generates a 1080×1920 "Story" PNG for news/events/column
 * records. On desktop a plain download works, but on mobile the user
 * actually wants to publish it to Instagram/WhatsApp/Facebook Stories.
 *
 * `shareStoryImage` fetches the PNG as a blob, wraps it in a File, and
 * invokes the Web Share Level 2 API (`navigator.share({ files })`) when
 * the browser supports it. The OS share sheet then lets the user pick
 * "Instagram → Stories" directly. Older browsers fall back to a tagged
 * download.
 *
 * Returns a Promise<string> indicating the branch taken:
 *   'shared'   — Web Share API dispatched successfully
 *   'downloaded' — Fallback <a download> triggered
 *   'cancelled' — User cancelled the system share sheet
 */
import { toast } from "sonner";

const isLikelyMobile = () =>
  typeof navigator !== "undefined" &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

export async function shareStoryImage({ url, filename, title, text }) {
  if (!url) return "noop";
  // Make the saved filename unique per click so iOS Photos / Android
  // Downloads never collide with (or appear to reuse) an earlier save.
  const ts = Date.now();
  const base = (filename || "sine81-story.png").replace(/\.png$/i, "");
  const name = `${base}-${ts}.png`;

  // ---------- Hard cache-bust ----------
  // Mobile browsers (iOS Safari in particular) frequently reuse cached
  // story images even when the server sends `Cache-Control: no-store`.
  // Users then see yesterday's layout instead of the latest typography.
  // Appending a per-click timestamp forces the browser (and any CDN/SW
  // in between) to treat the request as a brand-new resource.
  const cacheBuster = `_t=${ts}`;
  const bustedUrl = url + (url.includes("?") ? "&" : "?") + cacheBuster;

  // We fetch the PNG as a Blob so we can both save it and feed it to
  // navigator.share. Without this, Safari's download for inline PNG
  // responses often lands in Files instead of Photos, which blocks
  // the "post to Instagram Story" flow.
  let blob;
  try {
    const res = await fetch(bustedUrl, {
      credentials: "omit",
      cache: "no-store",
      // Explicitly set a 'reload' hint so service workers that implement
      // navigation preloads don't substitute a cached response.
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    blob = await res.blob();
  } catch (err) {
    // Network/CORS error — degrade to a plain anchor download.
    toast.error("Hikaye görseli alınamadı");
    _downloadFallback(bustedUrl, name);
    return "downloaded";
  }

  const file = new File([blob], name, { type: blob.type || "image/png" });

  // Prefer native share on supported mobile browsers.
  if (
    isLikelyMobile() &&
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({
        files: [file],
        title: title || "SİNE81",
        text: text || title || "",
      });
      return "shared";
    } catch (err) {
      if (err && err.name === "AbortError") return "cancelled";
      // Fall through to download if share fails for any other reason.
      // eslint-disable-next-line no-console
      console.warn("[shareStoryImage] navigator.share failed", err);
    }
  }

  // Desktop & unsupported mobile: save as a file + hint about the next step.
  _blobDownload(blob, name);
  if (isLikelyMobile()) {
    toast.success("Görsel indirildi. Instagram'da Hikaye olarak paylaşabilirsin.", {
      duration: 5000,
    });
  } else {
    toast.success("Görsel indirildi.");
  }
  return "downloaded";
}

function _blobDownload(blob, filename) {
  try {
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 2000);
  } catch {
    /* ignore; the explicit download fallback below handles the rest */
  }
}

function _downloadFallback(url, filename) {
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch { /* no-op */ }
}
